import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OperationDetail from './pages/OperationDetail';
import Documentation from './pages/Documentation';
import AdminPanel from './pages/AdminPanel';
import Analytics from './pages/Analytics';

// Components
import Layout from './components/Layout';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredPermission?: string }> = ({ 
  children, 
  requiredPermission 
}) => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div className="portal-loading" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Wird geladen…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only check permissions if requiredPermission is provided
  if (requiredPermission) {
    // Super admins bypass all permission checks
    const hasSuperAdminPermission = user?.permissions?.['firehub-super-admin'];
    
    if (!hasSuperAdminPermission && !user?.permissions?.[requiredPermission as keyof typeof user.permissions]) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div className="portal-loading" style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Wird geladen…
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/operation/:id" element={<OperationDetail />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route
          path="/analysis"
          element={
            <ProtectedRoute requiredPermission="asm-manage-analytics">
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App; 